import { Seo } from "../components/Seo";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, Headphones, Package, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";
import { categories, totalProductCount, CONTACT_INFO } from "../data/products";
import { VG_ORANGE, VG_ACCENT } from "../lib/brand";
import { HolidayPromo } from "../components/HolidayPromo";

const HERO_IMG = "/hero-home.jpg";

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo description="van Gelder, Inc. is your single source for commercial flooring — entrance matting, anti-fatigue flooring, custom logo mats, modular carpet tile, grid systems, and luxury vinyl planks and tile." />
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#777775]">
        <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover object-bottom" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/25 to-transparent" />
        <div className="relative mx-auto max-w-7xl px-4 py-24 md:py-32">
          <p className="font-heading text-sm font-semibold uppercase tracking-[0.25em] [text-shadow:0_2px_8px_rgba(0,0,0,0.6)]" style={{ color: VG_ACCENT }}>
            Solutions for Every Environment
          </p>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] max-w-3xl [text-shadow:0_3px_14px_rgba(0,0,0,0.65)]">
            Your Commercial Flooring <span style={{ color: VG_ORANGE }}>Partner</span>
          </h1>
          <p className="mt-6 max-w-xl text-base md:text-lg text-neutral-100 [text-shadow:0_2px_10px_rgba(0,0,0,0.75)]">
            From entrance matting and carpet tile to luxury vinyl and grid systems, van Gelder Inc.
            delivers high-performance flooring solutions designed for demanding commercial spaces.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button
              asChild
              className="text-white hover:opacity-90 h-12 px-7 text-base"
              style={{ backgroundColor: VG_ORANGE }}
              data-testid="hero-explore-cta"
            >
              <Link to="/products">
                Explore Flooring Solutions <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="h-12 px-7 text-base bg-white/10 text-white border-white/30 hover:bg-white/15 hover:text-[#A9C8E8]"
              data-testid="hero-contact-cta"
            >
              <Link to="/contact">
                <Headphones className="mr-2 h-5 w-5" /> Contact Customer Service
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="h-12 px-7 text-base bg-white/10 text-white border-white/30 hover:bg-white/15 hover:text-[#A9C8E8]"
              data-testid="hero-samples-cta"
            >
              <Link to="/contact">
                <Package className="mr-2 h-5 w-5" /> Request Samples
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <HolidayPromo />

      {/* Company intro */}
      <section className="bg-neutral-50 border-y border-neutral-200">
        <div className="mx-auto max-w-4xl px-4 py-16 text-center">
          <div className="mx-auto h-1 w-16 mb-5" style={{ backgroundColor: VG_ORANGE }} />
          <h2 className="font-heading text-3xl font-extrabold text-neutral-900">
            About van Gelder, Inc.
          </h2>
          <p className="mt-5 text-neutral-600 leading-relaxed">
            van Gelder, Inc. is your single source for commercial flooring solutions. We supply and
            service Entryway Matting, Anti-Fatigue Flooring, Custom/Logo and Commercial Matting,
            Modular Carpet Tiles, the TacFast® Carpet Tile System, Grid Matting Systems, and Luxury
            Vinyl Planks and Tile.
          </p>
          <Button asChild variant="outline" className="mt-8" data-testid="home-about-cta">
            <Link to="/about">Learn More About Us</Link>
          </Button>
        </div>
      </section>

      {/* Category cards */}
      <section className="mx-auto max-w-7xl px-4 py-16">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="h-1 w-16 mb-4" style={{ backgroundColor: VG_ORANGE }} />
            <h2 className="font-heading text-3xl font-extrabold text-neutral-900">Product Categories</h2>
            <p className="mt-2 text-neutral-600">
              Browse our {totalProductCount} products across {categories.length} categories.
            </p>
          </div>
          <Link to="/products" className="text-sm font-semibold hover:underline" style={{ color: VG_ORANGE }}>
            View all products →
          </Link>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/products?category=${cat.slug}`}
              data-testid={`home-category-${cat.slug}`}
              className="group overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5"
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src={cat.image}
                  alt={cat.name}
                  style={{ objectPosition: cat.imgPosition || "center" }}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </div>
              <div className="p-5">
                <h3 className="font-heading text-lg font-bold text-neutral-900 group-hover:text-[#ED8B00]">
                  {cat.name}
                </h3>
                <p className="mt-1.5 text-sm text-neutral-600 line-clamp-2">{cat.blurb}</p>
                <span className="mt-3 inline-flex items-center text-sm font-semibold text-[#ED8B00]">
                  {cat.products.length} product{cat.products.length === 1 ? "" : "s"} <ArrowRight className="ml-1 h-4 w-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Sustainability */}
      <section className="bg-[#eef1ea] border-y border-[#d9e0d2]" data-testid="home-sustainability">
        <div className="mx-auto max-w-7xl px-4 py-16 lg:py-20">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            {/* Copy */}
            <div>
              <div className="h-1 w-16 mb-5" style={{ backgroundColor: VG_ORANGE }} />
              <p className="flex items-center gap-2 font-heading text-sm font-semibold uppercase tracking-[0.18em] text-[#4d7c3f]">
                <Leaf className="h-4 w-4" /> Sustainability
              </p>
              <h2 className="mt-3 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-neutral-900 sm:text-5xl">
                Designed to last.<br />
                <span className="text-[#4d7c3f]">Made to matter.</span>
              </h2>
              <p className="mt-5 text-neutral-700 leading-relaxed">
                We believe lasting performance and environmental responsibility should be designed
                together. From recycled materials and low-VOC adhesives to products that may
                contribute toward LEED-focused projects, we carefully consider how flooring is made,
                installed, maintained, and used over time.
              </p>
              <p className="mt-4 text-neutral-700 leading-relaxed">
                Choosing van Gelder Inc. means investing in flooring created to perform longer and
                make more thoughtful use of materials. Our commitment extends beyond the product
                itself, supporting more responsible decisions from manufacturing and installation
                through the full life of the floor.
              </p>

              {/* Certification trust row */}
              <div className="mt-8">
                <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
                  Independently certified for low emissions &amp; indoor air quality
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-x-8 gap-y-5" data-testid="sustainability-certifications">
                  {[
                    { src: "/certifications/floorscore.svg", alt: "FloorScore SCS Certified" },
                    { src: "/certifications/greenguard-gold.svg", alt: "UL GREENGUARD Gold Certified" },
                    { src: "/certifications/eurofins-iac.svg", alt: "Eurofins Indoor Air Comfort" },
                    { src: "/certifications/anab.svg", alt: "ANAB Accredited" },
                  ].map((c) => (
                    <img
                      key={c.src}
                      src={c.src}
                      alt={c.alt}
                      className="h-14 w-auto object-contain"
                      loading="lazy"
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Imagery */}
            <div className="relative">
              <div className="overflow-hidden rounded-2xl border border-[#d9e0d2] shadow-lg">
                <img
                  src="/sustainability/biophilic-lobby-v2.jpg"
                  alt="Biophilic commercial lobby with natural light, greenery, and warm cork flooring"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Contact strip */}
      <section className="text-white" style={{ backgroundColor: VG_ORANGE }} data-testid="home-contact-strip">
        <div className="mx-auto max-w-7xl px-4 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold">Need help finding the right solution?</h2>
            <p className="mt-2 text-white/90">Our customer service team is ready to assist you.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button asChild className="bg-white text-neutral-900 hover:bg-neutral-100 h-12 px-6" data-testid="strip-contact-cta">
              <Link to="/contact">Contact Customer Service</Link>
            </Button>
            <Button asChild variant="outline" className="h-12 px-6 border-white text-white hover:bg-white/10 bg-transparent">
              <a href={CONTACT_INFO.phoneHref}>
                <Phone className="mr-2 h-5 w-5" /> {CONTACT_INFO.phone}
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
